import { Box, Card, Flex, Image, Text, Icon, Button } from '@opendoor/bricks/core';
import { Chip, ProgressBar, Modal } from '@opendoor/bricks/complex';
import { MarginProps } from '@opendoor/bricks/system/theme';
import { useState } from 'react';
import ReactPlayer from 'react-player';
import { isImageFile } from 'shared/helpers/s3Helpers';
import { OdProtosWebData_RecordedFeature as RecordedFeature } from '__generated__/globalTypes';
import { linkForUploadValidity } from 'sellDirect/components/ssva/LinkUploadModule';
import { useUpdateVirtualAssessmentRecordings } from 'sellDirect/graphql/useUpdateVirtualAssessmentRecordings';

const UPLOADED_LINK_ICON = (
  <Image
    src="https://imgdrop.imgix.net/5fa31d60917d44eea6a122aa2a224845.svg"
    alt=""
    width="28px"
    height="32px"
  />
);

const UPLOADED_FILE_ICON = (
  <Image
    src="https://imgdrop.imgix.net/d05bbc7b120f4715b3a3bb9ae8215f6f.png"
    alt=""
    width="48px"
    height="48px"
  />
);

// component-test-check-ignore
/** Card Component when video upload is ongoing, completed, or with error */
export const VideoUploadCardWithoutContext: React.FC<VideoUploadCardProps> = ({
  uploadCompleted,
  fileName,
  videoLink,
  progress,
  hasError,
  feature,
  deleteDisabled,
  clearRecordingsByFeature,
  ...marginProps
}) => {
  // TODO: remove fileDeletionEnabled when BE is ready and add a onDelete callback to FE
  const [displayErrorCard, setDisplayErrorCard] = useState(hasError);
  const [showVideoModal, setShowVideoModal] = useState<boolean>(false);

  // Indiates that this is a link upload rather than a file upload
  const { isValid: isValidLinkUpload } = linkForUploadValidity(videoLink || '');

  // Only show delete button for uploaded links
  const fileDeletionEnabled = isValidLinkUpload && !deleteDisabled;

  const onCardClick = isValidLinkUpload
    ? () => window.open(videoLink, '_blank')
    : () => setShowVideoModal(true);

  const onDeleteButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (clearRecordingsByFeature && feature) {
      clearRecordingsByFeature(feature, true);
    }
  };

  const linkAddedTag = (
    <Chip
      rightIcon={{ name: 'checkmark', color: 'neutrals0' }}
      borderColor="green50"
      backgroundColor="green50"
      text="Link Added"
      textColor="neutrals0"
      size="medium"
    />
  );

  const fileAddedTag = (
    <Chip
      rightIcon={{ name: 'checkmark', color: 'neutrals0' }}
      borderColor="green50"
      backgroundColor="green50"
      text="Added"
      textColor="neutrals0"
      size="medium"
      ml={[0, null, 4]}
    />
  );

  const getUploadIcon = () => {
    return isValidLinkUpload ? UPLOADED_LINK_ICON : UPLOADED_FILE_ICON;
  };

  return (
    <Box data-testid={fileName} width="100%" {...marginProps}>
      {displayErrorCard && (
        <Card
          height="auto"
          width="100%"
          variant="tertiary"
          borderColor="red50"
          px="7"
          py={['6', null, '7']}
          onClick={() => setDisplayErrorCard(false)}
          mt={5}
        >
          <Flex width="100%" justifyContent="space-between" alignItems="center">
            <Text color="red50"> An error has occurred </Text>
            <Icon name="close" color="red50" size={12} />
          </Flex>
        </Card>
      )}
      {!hasError && (
        <Button
          variant="ghost"
          onClick={onCardClick}
          aria-label="click to open file"
          analyticsName="VideoUploadCard"
          type="button"
        >
          <Card
            variant="tertiary"
            height="auto"
            width="100%"
            px="7"
            py={['6', null, '7']}
            borderColor="neutrals30"
            mt={5}
          >
            {/* left(video component) <> right(x) */}
            <Flex width="100%" justifyContent="space-between" alignItems="center">
              {/* left(thumbnail)right(title+ bar/Added) */}
              <Flex width="100%" justifyContent="left" alignItems="center">
                {getUploadIcon()}
                {uploadCompleted ? (
                  <Flex
                    px={5}
                    width="100%"
                    alignItems="flex-start"
                    flexDirection={isValidLinkUpload ? 'column' : ['column', 'column', 'row']}
                  >
                    <Box pr={2}>
                      <Text size="s1" wordBreak="break-word">
                        {fileName}
                      </Text>
                    </Box>
                    {isValidLinkUpload ? linkAddedTag : fileAddedTag}
                  </Flex>
                ) : (
                  <Flex px={5} width="100%" alignItems="flex-start" flexDirection="column">
                    <Box pb={4}>
                      <Text size="s1">
                        {fileName}
                        {' ... '}
                        {progress} % done
                      </Text>
                    </Box>
                    <ProgressBar thickness={3} progress={progress} gradientProgress={false} />
                  </Flex>
                )}
              </Flex>
              {fileDeletionEnabled && (
                <Flex alignItems="center" onClick={onDeleteButtonClick} cursor="pointer">
                  <Icon name="close" size={12} />
                </Flex>
              )}
            </Flex>
          </Card>
        </Button>
      )}
      {!hasError && uploadCompleted && videoLink && fileName && (
        <Modal
          analyticsName="cake-ssva-video-upload-card-video-modal"
          className="video-modal"
          isOpen={showVideoModal}
          light={true}
          showClose
          onRequestClose={() => setShowVideoModal(false)}
          desktopAndTabletSize="sm"
          hasPadding={true}
          headerProps={{ title: fileName, titleProps: { as: 'h2' } }}
        >
          {isImageFile(fileName) ? (
            <Flex justifyContent="center" width="100%">
              <Image src={videoLink} alt="The image that you just uploaded" maxWidth="100%" />
            </Flex>
          ) : (
            <ReactPlayer
              url={videoLink}
              playing={true}
              controls={true}
              width="100%"
              height="100%"
            />
          )}
        </Modal>
      )}
    </Box>
  );
};

export type VideoUploadCardProps = {
  uploadCompleted?: boolean;
  fileName?: string;
  videoLink?: string;
  progress?: number;
  hasError?: boolean;
  feature: RecordedFeature;
  deleteDisabled?: boolean;
  clearRecordingsByFeature?: (feature: RecordedFeature, clearAll: boolean) => void;
};

/** Different variations of card for video upload */
export const VideoUploadCard: React.FC<VideoUploadCardProps & MarginProps> = (props) => {
  const { clearLastSSVARecordingByFeature } = useUpdateVirtualAssessmentRecordings();
  return (
    <VideoUploadCardWithoutContext
      {...props}
      clearRecordingsByFeature={clearLastSSVARecordingByFeature}
    />
  );
};
